function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizarTag(tag) {
  return String(tag || '').toLowerCase().trim().replace(/\s+/g, '_');
}

function isHighRisk(value) {
  const text = normalize(value);
  return (
    text.includes('alto') ||
    text.includes('crítico') ||
    text.includes('critico') ||
    text.includes('negativo')
  );
}

// Extrae el tag de una narrativa: string viejo → null, objeto {texto,tag} → tag
function extraerTagDeNarrativa(narrativa) {
  if (!narrativa) return null;
  if (typeof narrativa === 'object' && narrativa.tag) {
    return normalizarTag(narrativa.tag);
  }
  return null;
}

export function buildNarrativeConvergence({
  signals = [],
  alerts = [],
  actors = [],
  timeline = []
}) {
  const validActors = actors.filter((actor) => {
    return (
      actor.estadoTerritorial ||
      actor.sensibilidadTerritorial ||
      actor.narrativaDominante ||
      actor.convergenciaNarrativa ||
      actor.activacionTerritorial ||
      actor.interpretacionEjecutiva
    );
  });

  const validSignals = signals.filter((signal) => {
    return signal.texto || signal.tags || signal.actor || signal.tipo;
  });

  const hasMinimumEvidence =
    validSignals.length >= 3 && validActors.length >= 1;

  if (!hasMinimumEvidence) {
    return {
      convergenceScore: 0,
      convergenceLevel: 'Preliminar',
      cognitiveConfidence: 'Baja',
      convergenceNarrative:
        'Información insuficiente para determinar convergencia territorial confiable. Se requiere mayor carga de señales, actores validados y contexto territorial.',
      dominantNarratives: [],
      dominantActors: [],
      dominantTerritories: [],
      criticalSignalsCount: validSignals.length,
      activeAlertsCount: alerts.length,
      sensitiveActorsCount: validActors.length,
      totalTimelineEvents: timeline.length,
      // NUEVO: convergencia por actor (vacía si no hay evidencia mínima)
      convergenciaPorActor: {}
    };
  }

  const narrativeCounter = {};
  const actorCounter = {};
  const territoryCounter = {};

  // Fuente 1 (original): tags de señales sociales
  validSignals.forEach((signal) => {
    String(signal.tags || '')
      .split(',')
      .map((tag) => normalize(tag))
      .filter(Boolean)
      .forEach((tag) => {
        narrativeCounter[tag] = (narrativeCounter[tag] || 0) + 1;
      });

    const actorKey = normalize(signal.actor);
    if (actorKey) actorCounter[actorKey] = (actorCounter[actorKey] || 0) + 1;

    const territoryKey = normalize(
      signal.ubicacion || signal.comuna || signal.sector
    );
    if (territoryKey)
      territoryCounter[territoryKey] =
        (territoryCounter[territoryKey] || 0) + 1;
  });

  // Fuente 2 (NUEVA): tags de narrativas de actores
  // Solo lee narrativas con tag (objeto {texto, tag}).
  // Strings sueltos (formato viejo) se ignoran — no se rompe nada.
  const tagsPorActor = {};
  actors.forEach((actor) => {
    const narrativas = Array.isArray(actor.narrativas) ? actor.narrativas : [];
    const tags = narrativas
      .map(extraerTagDeNarrativa)
      .filter(Boolean);

    if (tags.length) {
      tagsPorActor[actor.id] = { tags, influencia: actor.influencia || 'Baja' };
      tags.forEach((tag) => {
        narrativeCounter[tag] = (narrativeCounter[tag] || 0) + 1;
      });
    }
  });

  // Convergencia por actor: cuántos OTROS actores comparten su tag
  // Escala: 0 = null, 1 = Baja, 2-3 = Media, 4+ = Alta
  const conteoPorTag = {};
  Object.entries(tagsPorActor).forEach(([actorId, { tags }]) => {
    tags.forEach((tag) => {
      if (!conteoPorTag[tag]) conteoPorTag[tag] = new Set();
      conteoPorTag[tag].add(actorId);
    });
  });

  const convergenciaPorActor = {};
  Object.entries(tagsPorActor).forEach(([actorId, { tags }]) => {
    const maxOtros = Math.max(
      ...tags.map((tag) => (conteoPorTag[tag]?.size || 1) - 1)
    );
    if (maxOtros >= 4) convergenciaPorActor[actorId] = 'Alta';
    else if (maxOtros >= 2) convergenciaPorActor[actorId] = 'Media';
    else if (maxOtros >= 1) convergenciaPorActor[actorId] = 'Baja';
    else convergenciaPorActor[actorId] = null;
  });

  const criticalSignals = validSignals.filter(
    (signal) => isHighRisk(signal.sentimiento) || isHighRisk(signal.riesgo)
  );

  const sensitiveActors = validActors.filter(
    (actor) =>
      isHighRisk(actor.riesgo) ||
      isHighRisk(actor.estado) ||
      isHighRisk(actor.sensibilidadTerritorial) ||
      isHighRisk(actor.activacionTerritorial) ||
      isHighRisk(actor.convergenciaNarrativa)
  );

  const repeatedNarratives = Object.values(narrativeCounter).filter(
    (count) => count >= 2
  ).length;

  const convergenceScore =
    criticalSignals.length * 2 +
    sensitiveActors.length * 2 +
    repeatedNarratives * 3;

  let convergenceLevel = 'Bajo';
  let cognitiveConfidence = 'Media';
  let convergenceNarrative =
    'Existen señales territoriales registradas, pero todavía no se observa convergencia narrativa significativa.';

  if (convergenceScore >= 12) {
    convergenceLevel = 'Alto';
    cognitiveConfidence = 'Media';
    convergenceNarrative =
      'Se observa convergencia territorial relevante basada en señales críticas, actores cognitivamente validados y narrativas repetidas.';
  } else if (convergenceScore >= 6) {
    convergenceLevel = 'Medio';
    convergenceNarrative =
      'Se observan indicios de convergencia narrativa y sensibilidad territorial emergente.';
  }

  return {
    convergenceScore,
    convergenceLevel,
    cognitiveConfidence,
    convergenceNarrative,
    dominantNarratives: Object.entries(narrativeCounter)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count })),
    dominantActors: Object.entries(actorCounter)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count })),
    dominantTerritories: Object.entries(territoryCounter)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, count]) => ({ name, count })),
    criticalSignalsCount: criticalSignals.length,
    activeAlertsCount: alerts.length,
    sensitiveActorsCount: sensitiveActors.length,
    totalTimelineEvents: timeline.length,
    // NUEVO: convergencia real por actor desde tags de narrativas
    convergenciaPorActor
  };
}